/**
 * The "Sample image shown …" caption, in ONE place.
 *
 * A product photo can only ever show one configuration, but the customer is choosing
 * others — a different centre carat, and (new, 2026-08-29) a different band width. The
 * caption says which one they are looking at.
 *
 * It lives here rather than inline in AddCart because THREE surfaces render it: the
 * storefront, and the two admin pages that each preview it while you type
 * (ProductEdit for ordinary products, EngagementAdd for engagement builds). When the
 * wording lived in each of them separately the preview was free to drift from what the
 * customer actually sees.
 *
 * Adnan's spec, 2026-08-29:
 *   carat only  → "Sample image shown with a 1.00ct diamond."
 *   width only  → "Sample image shown for 8mm width."
 *   both        → "Sample image shown for 8mm width and 1.00ct diamond."
 *   neither     → nothing at all
 *
 * THE WIDTH IS ALWAYS STATED, on both surfaces (Adnan, 2026-08-29). Where it comes from
 * differs — the photo quotes `sampleImageWidth`, the 3D viewer quotes the width of the
 * model actually on screen — but neither hides it.
 *
 * It was briefly conditional on the photo, appearing only when the customer's chosen width
 * differed. That looked tidy and read as a bug: the line vanished and reappeared as you
 * moved between the model and the photo. On a piece sold in several widths, "which one am
 * I looking at" is worth answering even when the answer is "the one you picked".
 *
 * The CARAT clause is still suppressed when it matches the selection — there the noise
 * argument holds, because a photo shot at 1ct next to a 1ct selection tells you nothing.
 */

/**
 * Tidy a width the admin typed. The field is free text so it can match however the
 * widths are labelled ("8mm", "8.0MM", "8"), but a bare number would render as
 * "shown for 8 width", so a unit is added when one is missing. Anything that already
 * carries a unit is left exactly as typed.
 *
 * ⚠️ THE UNIT IS EDITABLE (Adnan, 2026-09-07) — it used to be hard-coded "mm", which is
 * wrong for anything sold in inches. The picture pendant is sized 1" / 1.5" / 2" and came
 * out as `1.5"mm`, because an inch mark carries no letter and so read as "no unit yet".
 *
 * `unit` comes from the product's own `sampleImageWidthUnit` box and DEFAULTS TO "mm", so
 * every product that predates the field renders exactly as it did before. Checked against
 * all 32 live products carrying a width — 7 distinct values ("4mm", "6mm", "14mm",
 * "8.0MM", "13.0MM", "14", "38") — and all 7 are byte-identical under the new rule.
 *
 * Two things stay untouched on purpose:
 *   · a value that already carries its own unit is still returned verbatim, which is what
 *     the 21 products storing "4mm"/"6mm"/"14mm" rely on — the unit box is ignored there
 *     rather than doubling it up into "4mmmm";
 *   · the 3D viewer calls this with no unit at all (it quotes the width of the model on
 *     screen, a different thing from the photo's field), so it keeps the "mm" default.
 */
export function normaliseWidth(raw, unit) {
  const w = String(raw ?? "").trim();
  if (!w) return "";
  // Already carries a unit — letters ("8mm", "Standard") or an inch/foot mark (1.5", 1.5″).
  if (/[a-z]/i.test(w) || /["'″′]/.test(w)) return w;
  const u = String(unit ?? "").trim() || "mm";
  // "8"+"mm" -> "8mm" exactly as before; a spelled-out unit gets a space so a customer
  // reads "1.5 inch width", not "1.5inch width".
  return `${w}${/^[a-z]{3,}$/i.test(u) ? " " : ""}${u}`;
}

/**
 * Do a selected value and the photographed one describe the same thing?
 *
 * Compared NUMERICALLY wherever both sides contain a number, because the two are typed
 * in different places and never match as strings: the sample width is free text ("8mm")
 * while the selected one is a width LABEL off the recipe ("8.0MM"), and the sample carat
 * is a string ("1.00") while the selection is a number (1). Anything with no number in
 * it falls back to a loose string compare so a label like "Standard" still works.
 *
 * Returns false when either side is missing — "unknown" must never read as "matches", or
 * the caption would hide itself on a product that has no selector at all.
 */
function sameValue(selected, sample) {
  if (selected == null || sample == null) return false;
  const a = String(selected).trim(), b = String(sample).trim();
  if (!a || !b) return false;
  const na = parseFloat(a.replace(/[^\d.]/g, "")), nb = parseFloat(b.replace(/[^\d.]/g, ""));
  if (Number.isFinite(na) && Number.isFinite(nb)) return Math.abs(na - nb) < 1e-6;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Build the caption. Returns "" when there is nothing to say, so callers can render
 * `caption && <p>{caption}</p>` and get no empty paragraph.
 *
 * `source` is whichever object carries the fields — the product itself for an ordinary
 * product, or product.engagement for an engagement build.
 *
 * `selection` is what the customer currently has chosen, `{ carat, width }`. The caption
 * only mentions a dimension the customer has moved AWAY from: telling someone the photo
 * shows the 8mm when they are looking at the 8mm is noise. Each clause is dropped
 * independently, so choosing a different carat but the photographed width leaves just the
 * carat half of the sentence.
 *
 * Omit `selection` entirely (admin previews, ordinary products with no configurator) and
 * nothing is filtered — the full sentence comes back, which is the old behaviour.
 */
export function sampleImageCaption(source, selection, view) {
  const isModel = view?.mode === "model";
  const noun = isModel ? "model" : "image";

  let ct = String(source?.sampleImageCaratSize ?? "").trim();
  // In 3D the width comes from the MODEL being shown, not from the photo's field — they
  // are different things and a product can easily have one without the other.
  // The unit box belongs to the PHOTO's width. In 3D the width comes from the model on
  // screen, which is always the recipe's own mm figure, so that path keeps the default.
  let width = isModel
    ? normaliseWidth(view?.modelWidth)
    : normaliseWidth(source?.sampleImageWidth, source?.sampleImageWidthUnit);

  // CARAT is still suppressed when it matches what the customer picked — telling someone
  // the photo shows the 1ct they already chose is noise.
  if (sameValue(selection?.carat, ct)) ct = "";
  // WIDTH is always stated, on both surfaces (Adnan, 2026-08-29). It briefly hid itself on
  // the photo when the selected width matched, which made the caption vanish as you moved
  // between the model and the photo and read as a bug rather than as restraint. Stating it
  // every time is predictable, and on a piece sold in several widths "which one am I
  // looking at" is worth answering even when the answer is "the one you picked".

  if (width && ct) return `Sample ${noun} shown for ${width} width and ${ct}ct diamond.`;
  if (width) return `Sample ${noun} shown for ${width} width.`;
  if (ct) return `Sample ${noun} shown with a ${ct}ct diamond.`;
  return "";
}

/**
 * Pick the 3D model to show for the width the customer has chosen.
 *
 * Order: a model tagged with that exact width → an UNTAGGED model (the fallback, for any
 * width with no model of its own) → the legacy top-level `model3d`, which is what every
 * product had before per-width models existed and must keep working untouched.
 *
 * Returns `{ embedId, width, tilt }`, or null when the product has no usable model.
 * `width` is "" for the fallback and legacy cases — the caller then knows not to claim a
 * width it cannot actually vouch for.
 */
export function modelForWidth(product, selectedWidth) {
  const list = Array.isArray(product?.models3d) ? product.models3d.filter((m) => m && m.path) : [];
  const tagged = list.find((m) => m.width && sameValue(selectedWidth, normaliseWidth(m.width)));
  const chosen = tagged || list.find((m) => !m.width);
  if (chosen) return { embedId: chosen.render_embed_id || "", width: chosen.width || "", tilt: chosen.tilt !== false };
  // The legacy fallback needs BOTH a model and an embed. Checking the embed alone would
  // resurrect a removed model: an embed id can outlive the model3d it was minted from.
  if (product?.render_embed_id && product?.model3d?.path) {
    return { embedId: product.render_embed_id, width: "", tilt: product?.model3d?.tilt !== false };
  }
  return null;
}

/**
 * Pick the object the caption should read from. Engagement rings carry the fields on
 * `engagement` (set in EngagementAdd), ordinary products at the TOP LEVEL (set in
 * ProductEdit). A product is never both, so first match wins. `showSampleImageCaption`
 * is the admin toggle that gates each copy.
 */
export function captionSourceFor(product) {
  if (product?.engagement?.showSampleImageCaption) return product.engagement;
  if (product?.showSampleImageCaption) return product;
  return null;
}
